// eslint-disable-next-line import/namespace
import {
  FeatureDetail,
  FeatureGroupKey,
  FeatureMetadata,
  FeatureMetadataMap,
  FeatureRequiredMap,
  Features,
  Product,
  ProductName,
} from "@zp-models/product";

interface FeatureTranslationDefinition {
  slug: string;
  features: Record<string, string>;
}

const featureTranslationKey = (
  groupSlug: string,
  featureSlug: string,
  field: "LABEL" | "SUB_LABEL" | "INFO_TEXT"
): string => `PRODUCTS.FEATURES.${groupSlug}.${featureSlug}.${field}`;

export const FEATURE_TRANSLATION_MAP: Record<FeatureGroupKey, FeatureTranslationDefinition> = {
  mobilityOptions: {
    slug: "MOBILITY_OPTIONS",
    features: {
      publicTransport: "PUBLIC_TRANSPORT",
      sharedTransport: "SHARED_TRANSPORT",
      taxi: "TAXI",
      parking: "PARKING",
      tripRegistration: "TRIP_REGISTRATION",
      homeWorkRegistration: "HOME_WORK_REGISTRATION",
      workplaceRegistration: "WORKPLACE_REGISTRATION",
      mobilityHubs: "MOBILITY_HUBS",
      bikeLease: "BIKE_LEASE",
      otherDeclarations: "OTHER_DECLARATIONS",
    },
  },
  reports: {
    slug: "REPORTS",
    features: {
      travelBehaviorReport: "TRAVEL_BEHAVIOR_REPORT",
      costsReport: "COSTS_REPORT",
      co2: "CO2",
      payroll: "PAYROLL",
      fiscalRollout: "FISCAL_ROLLOUT",
      detailedAndCustom: "DETAILED_AND_CUSTOM",
    },
  },
  integrations: {
    slug: "INTEGRATIONS",
    features: {
      manualUserManagement: "MANUAL_USER_MANAGEMENT",
      fileIntegration: "FILE_INTEGRATION",
      apiIntegrationHR: "API_INTEGRATION_HR",
      apiIntegrationSalary: "API_INTEGRATION_SALARY",
    },
  },
  policy: {
    slug: "POLICY",
    features: {
      basicRules: "BASIC_RULES",
      advancedRules: "ADVANCED_RULES",
      roles: "ROLES",
      sampling: "SAMPLING",
    },
  },
  authentication: {
    slug: "AUTHENTICATION",
    features: {
      oneTimePassword: "ONE_TIME_PASSWORD",
      singleSignOn: "SINGLE_SIGN_ON",
    },
  },
  personalization: {
    slug: "PERSONALIZATION",
    features: {
      logo: "LOGO",
      customOnboarding: "CUSTOM_ONBOARDING",
    },
  },
  serviceAndProjectManagement: {
    slug: "SERVICE_AND_PROJECT_MANAGEMENT",
    features: {
      twentyFourSevenSupport: "TWENTY_FOUR_SEVEN_SUPPORT",
      implementationProjectManagement: "IMPLEMENTATION_PROJECT_MANAGEMENT",
      personalAdviceAndManagement: "PERSONAL_ADVICE_AND_MANAGEMENT",
    },
  },
};

export const FEATURE_METADATA: FeatureMetadataMap = Object.entries(FEATURE_TRANSLATION_MAP).reduce(
  (accumulator, [groupKey, config]) => {
    const group = groupKey as FeatureGroupKey;
    accumulator[group] = Object.entries(config.features).reduce(
      (groupAccumulator, [featureKey, featureSlug]) => {
        groupAccumulator[featureKey] = {
          labelKey: featureTranslationKey(config.slug, featureSlug, "LABEL"),
          subLabelKey: featureTranslationKey(config.slug, featureSlug, "SUB_LABEL"),
          infoTextKey: featureTranslationKey(config.slug, featureSlug, "INFO_TEXT"),
        } satisfies FeatureMetadata;
        return groupAccumulator;
      },
      {} as Record<string, FeatureMetadata>
    );
    return accumulator;
  },
  {} as FeatureMetadataMap
);

const START_FEATURE_FLAGS: FeatureRequiredMap = {
  mobilityOptions: {
    publicTransport: true,
    sharedTransport: true,
    taxi: true,
    parking: true,
  },
  reports: {
    travelBehaviorReport: true,
    costsReport: true,
    co2: true,
  },
  integrations: {
    manualUserManagement: true,
  },
  authentication: {
    oneTimePassword: true,
  },
  serviceAndProjectManagement: {
    twentyFourSevenSupport: true,
  },
};

const PLUS_FEATURE_FLAGS: FeatureRequiredMap = {
  mobilityOptions: {
    publicTransport: true,
    sharedTransport: true,
    taxi: true,
    parking: true,
    tripRegistration: true,
    homeWorkRegistration: true,
  },
  reports: {
    travelBehaviorReport: true,
    costsReport: true,
    co2: true,
    payroll: true,
  },
  integrations: {
    manualUserManagement: true,
  },
  policy: {
    basicRules: true,
    roles: true,
    sampling: true,
  },
  authentication: {
    oneTimePassword: true,
  },
  serviceAndProjectManagement: {
    twentyFourSevenSupport: true,
  },
};

const PRO_FEATURE_FLAGS: FeatureRequiredMap = {
  mobilityOptions: {
    publicTransport: true,
    sharedTransport: true,
    taxi: true,
    parking: true,
    tripRegistration: true,
    homeWorkRegistration: true,
    workplaceRegistration: true,
    mobilityHubs: true,
    bikeLease: true,
    otherDeclarations: true,
  },
  reports: {
    travelBehaviorReport: true,
    costsReport: true,
    co2: true,
    payroll: true,
    fiscalRollout: true,
    detailedAndCustom: true,
  },
  integrations: {
    manualUserManagement: true,
    fileIntegration: true,
    apiIntegrationHR: true,
    apiIntegrationSalary: true,
  },
  policy: {
    basicRules: true,
    advancedRules: true,
    roles: true,
    sampling: true,
  },
  authentication: {
    oneTimePassword: true,
    singleSignOn: true,
  },
  personalization: {
    logo: true,
    customOnboarding: true,
  },
  serviceAndProjectManagement: {
    twentyFourSevenSupport: true,
    implementationProjectManagement: true,
    personalAdviceAndManagement: true,
  },
};

export const PRODUCT_FEATURE_FLAGS: Record<ProductName, FeatureRequiredMap> = {
  Start: START_FEATURE_FLAGS,
  Plus: PLUS_FEATURE_FLAGS,
  Pro: PRO_FEATURE_FLAGS,
  Contact: {},
};

type ProductDefinition = Omit<Product, "features"> & {
  featureFlags: FeatureRequiredMap;
};

const PRODUCT_DEFINITIONS: ProductDefinition[] = [
  {
    id: 1,
    name: "Start",
    priceInCents: 0,
    pricedPerUnit: {
      user: 1,
      period: "month",
    },
    featureFlags: START_FEATURE_FLAGS,
  },
  {
    id: 2,
    name: "Plus",
    priceInCents: 236,
    pricedPerUnit: {
      user: 1,
      period: "month",
    },
    featureFlags: PLUS_FEATURE_FLAGS,
  },
  {
    id: 3,
    name: "Pro",
    priceInCents: 364,
    pricedPerUnit: {
      user: 1,
      period: "month",
    },
    featureFlags: PRO_FEATURE_FLAGS,
  },
];

export const buildFeatures = (requiredMap: FeatureRequiredMap): Features => {
  return Object.entries(FEATURE_METADATA).reduce(
    (featuresAccumulator, [groupKey, groupMetadata]) => {
      const group = groupKey as FeatureGroupKey;
      const groupRequirements = requiredMap[group] ?? {};

      featuresAccumulator[group] = Object.entries(groupMetadata).reduce(
        (groupFeatures, [featureKey, metadata]) => {
          groupFeatures[featureKey] = {
            required: Boolean(groupRequirements[featureKey]),
            ...metadata,
          } satisfies FeatureDetail;
          return groupFeatures;
        },
        {} as Record<string, FeatureDetail>
      );

      return featuresAccumulator;
    },
    {} as Features
  );
};

export const createDefaultProducts = (): Product[] =>
  PRODUCT_DEFINITIONS.map(({ featureFlags, ...product }) => ({
    ...product,
    features: buildFeatures(featureFlags),
  }));
